import { Controller } from '@hotwired/stimulus';

/**
 * section-collapse — sous `md`, chaque section d'un document se replie derrière
 * son titre devenu bouton (revue mobile WhatsApp, retouché le 2026-10-10).
 * Monté par `Documents::Chrome::ShellComponent` quand l'hôte lève
 * `collapsible_below_md` (seule la page agence du CR le fait).
 *
 * LE SERVEUR REPLIE, LE JS OUVRE. Chaque section arrive avec `data-collapsed` et
 * son corps porte une variante `max-md:` qui le masque tant que son parent porte
 * l'attribut : aucun éclair de contenu ouvert avant le JS, et rien ne change au-dessus
 * de `md`. Ce contrôleur n'écrit QUE `data-collapsed` (section) et `aria-expanded`
 * (bouton) : aucune classe Tailwind en JS.
 *
 * L'état vit sur le `<section>`, qu'aucun flux Turbo ne remplace (les flux visent
 * les ids intérieurs des corps). `sectionTargetConnected` réapplique quand même la
 * mémoire si une section venait à (re)connecter.
 *
 * Targets:
 *   section — chaque `<section id>` repliable (porte `data-collapsed` replié).
 *   toggle  — le bouton de titre de la section (`aria-expanded`, `aria-controls`).
 *
 * Values:
 *   storageKey — clé `localStorage` (ex. `cr-sections-open:<report_id>`) qui garde
 *                la liste JSON des ids de section OUVERTS, par appareil et par
 *                document. Vide : aucune mémoire. Chaque accès est sous try/catch :
 *                sans stockage, tout reste replié et le bouton marche.
 *
 * Actions:
 *   toggle — ouvre ou replie la section du bouton cliqué. Sous `md`, sous-titre et
 *            barre d'outils du titre ne s'affichent que section ouverte (CSS) : rien
 *            à piloter ici.
 *
 * Navigation (écouteurs globaux, retirés en `disconnect`) : toute cible de hash
 * située DANS une section repliée ouvre cette section.
 *   - au `connect` : le hash de la page (`/comptes-rendus/13#section-effectifs`).
 *     Le défilement reste à `cr-anchor-scroll` (racine de la page), dont le cadre
 *     d'animation arrive après cette ouverture synchrone ;
 *   - au clic sur un lien `#…` (le sommaire téléphone, hors de cet élément) : on
 *     ouvre AVANT que le navigateur ne défile vers l'ancre ;
 *   - au `hashchange` qui n'est pas passé par un clic : on ouvre, puis on défile
 *     vers la cible (sans animation si l'utilisateur la refuse).
 *
 * @example
 * <div data-controller="section-collapse"
 *      data-section-collapse-storage-key-value="cr-sections-open:13">
 *   <section id="section-effectifs" data-section-collapse-target="section" data-collapsed>
 *     <h3><button type="button" aria-expanded="false" aria-controls="section-effectifs-body"
 *                 data-section-collapse-target="toggle"
 *                 data-action="section-collapse#toggle">4. Effectifs</button></h3>
 *     <div id="section-effectifs-body" class="max-md:[[data-collapsed]>&]:hidden">…</div>
 *   </section>
 * </div>
 */
export default class extends Controller {
  static targets = ['section', 'toggle'];
  static values = { storageKey: { type: String, default: '' } };

  connect() {
    this.onHashChange = () => this.#revealHash({ scroll: true });
    this.onDocumentClick = (event) => this.#revealFromLink(event);
    window.addEventListener('hashchange', this.onHashChange);
    document.addEventListener('click', this.onDocumentClick);
    this.sectionTargets.forEach((section) => this.#applyStored(section));
    this.#revealHash({ scroll: false });
  }

  disconnect() {
    window.removeEventListener('hashchange', this.onHashChange);
    document.removeEventListener('click', this.onDocumentClick);
  }

  sectionTargetConnected(section) {
    this.#applyStored(section);
  }

  toggle(event) {
    const section = this.#sectionContaining(event.currentTarget);
    if (!section) return;
    this.#setOpen(section, this.#isCollapsed(section));
  }

  // ── navigation ──────────────────────────────────────────────────────────────

  #revealFromLink(event) {
    const link = event.target?.closest?.('a[href^="#"]');
    if (!link) return;
    this.#reveal(this.#decode(link.getAttribute('href').slice(1)));
  }

  #revealHash({ scroll }) {
    const id = this.#decode(window.location.hash.slice(1));
    const target = this.#reveal(id);
    if (scroll && target) this.#scrollTo(target);
  }

  /**
   * Ouvre la section repliée qui contient l'élément `id`.
   * @returns {Element|null} l'élément visé, SEULEMENT s'il a fallu ouvrir.
   */
  #reveal(id) {
    if (!id) return null;
    const target = document.getElementById(id);
    if (!target) return null;
    const section = this.#sectionContaining(target);
    if (!section || !this.#isCollapsed(section)) return null;
    this.#setOpen(section, true);
    return target;
  }

  #scrollTo(target) {
    if (typeof target.scrollIntoView !== 'function') return;
    const reduce = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }

  // Le hash sans son `#`, décodé (un id accentué arrive encodé dans l'URL).
  #decode(hash) {
    if (!hash) return '';
    try {
      return decodeURIComponent(hash);
    } catch {
      return hash;
    }
  }

  // ── état ────────────────────────────────────────────────────────────────────

  #sectionContaining(node) {
    if (!node) return null;
    return (
      this.sectionTargets.find((section) => section === node || section.contains(node)) ?? null
    );
  }

  #isCollapsed(section) {
    return section.hasAttribute('data-collapsed');
  }

  #setOpen(section, open, { persist = true } = {}) {
    if (open) {
      section.removeAttribute('data-collapsed');
    } else {
      section.setAttribute('data-collapsed', '');
    }
    const toggle = this.toggleTargets.find((candidate) => section.contains(candidate));
    toggle?.setAttribute('aria-expanded', String(open));
    if (persist) this.#persist(section.id, open);
  }

  // ── mémoire par appareil ────────────────────────────────────────────────────

  #applyStored(section) {
    if (!this.storageKeyValue || !section?.id) return;
    if (this.#readStored().has(section.id)) this.#setOpen(section, true, { persist: false });
  }

  #persist(id, open) {
    if (!this.storageKeyValue || !id) return;
    const stored = this.#readStored();
    if (open) {
      stored.add(id);
    } else {
      stored.delete(id);
    }
    this.#writeStored(stored);
  }

  /** @returns {Set<string>} les ids ouverts ; vide si illisible ou corrompu. */
  #readStored() {
    try {
      const parsed = JSON.parse(globalThis.localStorage.getItem(this.storageKeyValue) || '[]');
      return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []);
    } catch {
      // Stockage bloqué (navigation privée, cookies refusés) ou JSON corrompu.
      return new Set();
    }
  }

  #writeStored(ids) {
    try {
      globalThis.localStorage.setItem(this.storageKeyValue, JSON.stringify([...ids]));
    } catch {
      // Stockage bloqué ou plein : le repli marche, sans mémoire.
    }
  }
}
